// ---------------------------------------------------------------------------
// TanStack Query hooks for informational text bank API
// ---------------------------------------------------------------------------

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  InformationalText,
  CreateInfoTextRequest,
  UpdateInfoTextRequest,
  InfoTextFilters,
} from '@/types/question';
import type { PaginatedResponse } from '@/types/common';

const INFO_TEXTS_KEY = 'informational-texts';

export function useInfoCategories() {
  return useQuery({
    queryKey: [INFO_TEXTS_KEY, 'categories'],
    queryFn: () => api.get<string[]>('/informational-texts/categories'),
    staleTime: 5 * 60 * 1000, // 5 min — rarely changes
  });
}

function buildQueryString(filters: InfoTextFilters): string {
  const params = new URLSearchParams();
  if (filters.keyword) params.set('keyword', filters.keyword);
  if (filters.category) params.set('category', filters.category);
  // SE-API-18: standard page/pageSize pagination.
  params.set('page', String(filters.page ?? 1));
  params.set('pageSize', String(filters.pageSize ?? 25));
  return params.toString();
}

export function useInformationalTexts(filters: InfoTextFilters) {
  return useQuery({
    queryKey: [INFO_TEXTS_KEY, filters],
    queryFn: () =>
      api.get<PaginatedResponse<InformationalText>>(
        `/informational-texts?${buildQueryString(filters)}`,
      ),
  });
}

export function useCreateInformationalText() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateInfoTextRequest) =>
      api.post<InformationalText>('/informational-texts', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INFO_TEXTS_KEY] });
    },
  });
}

export function useUpdateInformationalText() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ textId, data }: { textId: number; data: UpdateInfoTextRequest }) =>
      api
        .patch<{ entries: InformationalText[] }>(`/informational-text`, {
          entries: [{ id: textId, ...data }],
        })
        .then((r) => r.entries[0]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INFO_TEXTS_KEY] });
    },
  });
}

export function useDeleteInformationalText() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (textId: number) =>
      api.delete(`/informational-text`, { entries: [{ id: textId }] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INFO_TEXTS_KEY] });
    },
  });
}
